import React, { useState, useEffect, useCallback, useRef, FunctionComponent } from 'react';
import { showRecruitment } from '../../../services/recruitments';
import { pushDataLayer } from '../../../utils/gtm';
import { Presentation } from './presentation';
import type { Props } from './types';
import type { Recruitment } from '../../../types/recruitments';

export const Component: FunctionComponent<Props> = (props) => {
  const [recruitmentData, setRecruitmentData] = useState<Recruitment>({} as Recruitment);
  const containerRef = useRef<HTMLLIElement>(null);
  const DATA_VALUE_TAGS = [
    { flag: 'isScouted', camel: 'ScoutTag', suffix: 'scout_tag' },
    { flag: 'isApplied', camel: 'EntryTag', suffix: 'entry_tag' },
    { flag: 'isNew', camel: 'NewTag', suffix: 'new_tag' },
    { flag: 'regularEmployee', camel: 'PermanentTag', suffix: 'permanent_tag' },
    { flag: 'activeDateFixed', camel: 'ActiveTag', suffix: 'active_tag' },       // アクティブな企業
    { flag: 'hasRecordOfHiring', camel: 'HiringTag', suffix: 'hiring_tag' },     // 採用実績あり
    { flag: 'attentionPeople', camel: 'AttentionTag', suffix: 'attention_tag' }, // 注目タグ
    { flag: 'isHighResponse', camel: 'HighResponseTag', suffix: 'high_response_tag' },
  ] as const;

  useEffect(() => {
    const fetchRecruitment = async () => {
      try {
        const data = await showRecruitment(props.recruitment_id);
        setRecruitmentData(data);
        if (props.onLoad) {
          const firstImageUrl = data.images?.[0]?.url;
          if (firstImageUrl) {
            const img = new Image();
            img.onload = props.onLoad;
            img.onerror = props.onLoad;
            img.src = firstImageUrl;
          } else {
            props.onLoad();
          }
        }
      } catch (error) {
        console.error('Failed to fetch recruitment data:', error);
        props.onLoad?.();
      }
    };
    fetchRecruitment();
  }, [props.recruitment_id]);

  useEffect(() => {
    if (!recruitmentData.id || !containerRef.current) return;
    const el = containerRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        pushDataLayer({
          event: 'recruitments on screen',
          'data-category': pageCategory(),
          'data-value': pageValue(),
          'data-recruitmentId': recruitmentData.id,
          'data-companyId': recruitmentData.company_id,
          'data-list-type': props.dataListType ?? 'normal',
        });
        observer.disconnect();
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [recruitmentData.id]);

  const handleSetSession = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      const recruitmentId = event.currentTarget.dataset.recruitmentid;
      sessionStorage.setItem(
        location.href,
        JSON.stringify({ value: window.scrollY, recruitmentId: recruitmentId, page: props.page })
      );
    },
    [props.page]
  );

  const handleAnchorClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      const target = event.target as HTMLElement;

      // slick-dots 上のクリックは遷移させない
      if (target.closest('.slick-dots')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      handleSetSession(event);
    },
    [handleSetSession]
  );

  const pageCategory = useCallback((): any => {
    const url = window.location.href;
    const isSingleImage = recruitmentData.images?.length === 1;
    const effectiveVariant = isSingleImage ? 'single' : 'thumbsSlider';
    const variantSuffix =
      effectiveVariant && effectiveVariant !== 'single'
        ? `_${effectiveVariant}`
        : '';

    if (typeof props.dataCategory === 'string') {
      return `${props.dataCategory}${variantSuffix}`;
    }

    if (url.includes('/companies') && url.includes('/recruitments')) {
      return props.isStopped
        ? `${props.dataCategory.stopCompanyRecruitmentList}${variantSuffix}`
        : `${props.dataCategory.companyRecruitmentList}${variantSuffix}`;
    } else if (url.includes('/brands')) {
      return `${props.dataCategory.brandContent}${variantSuffix}`;
    }

    return `${props.dataCategory}${variantSuffix}`;
  }, [
    props.dataCategory,
    props.isStopped,
    recruitmentData.images,
  ]);

  const pageValue = useCallback((): any => {
    if (typeof props.dataValue === 'string') {
      return props.dataValue;
    }

    const baseKey =
      props.sort === 'new'
        ? 'newRecruitments'
        : props.sort === 'popular'
          ? 'popularRecruitments'
          : 'recruitments';
    const allKey = `${baseKey}AllTag`;
    const baseValue = props.dataValue[baseKey] ?? baseKey;

    const activeTags = DATA_VALUE_TAGS.filter((tag) => {
      // attention_tagはUI表示と連動させる（ABテスト制御）
      // isShowAttentionPeople=falseのとき注目タグUIは非表示になるため、data-valueからも除外する
      if (tag.camel === 'AttentionTag') {
        return (props.isShowAttentionPeople && recruitmentData.attentionPeople > 5) && recruitmentData[tag.flag];
      }
      return recruitmentData[tag.flag];
    });
    if (activeTags.length === 0) {
      return baseValue;
    }

    if (activeTags.length === DATA_VALUE_TAGS.length && props.dataValue[allKey]) {
      return props.dataValue[allKey];
    }

    const dynamicKey = `${baseKey}${activeTags.map((tag) => tag.camel).join('')}`;
    if (props.dataValue[dynamicKey]) {
      return props.dataValue[dynamicKey];
    }

    const entryKey = baseKey === 'newRecruitments' ? 'newRecrutimentsEntryTag' : `${baseKey}EntryTag`;
    const singleTagValues = {
      ScoutTag: props.dataValue[`${baseKey}ScoutTag`],
      EntryTag: props.dataValue[entryKey],
      NewTag: props.dataValue[`${baseKey}NewTag`],
      PermanentTag: props.dataValue[`${baseKey}PermanentTag`],
      ActiveTag: props.dataValue[`${baseKey}ActiveTag`],       // アクティブな企業
      HiringTag: props.dataValue[`${baseKey}HiringTag`],       // 採用実績あり
      AttentionTag: props.dataValue[`${baseKey}AttentionTag`], // 注目タグ
      HighResponseTag: props.dataValue[`${baseKey}HighResponseTag`],
    } as const;

    const knownValues = activeTags
      .map((tag) => singleTagValues[tag.camel])
      .filter((value): value is string => typeof value === 'string' && value.length > 0);

    if (knownValues.length === activeTags.length) {
      const basePrefix = `${baseValue}_`;
      const compactSuffixes = knownValues.map((value) =>
        value.startsWith(basePrefix) ? value.slice(basePrefix.length) : value
      );
      return `${baseValue}__${compactSuffixes.join('__')}`;
    }

    return `${baseValue}__${activeTags.map((tag) => tag.suffix).join('__')}`;
  }, [DATA_VALUE_TAGS, props.dataValue, props.sort, recruitmentData]);

  const presentationsProps = {
    ...props,
    recruitmentData,
    pageCategory,
    pageValue,
    handleSetSession,
    handleAnchorClick,
    containerRef,
  };

  return <Presentation {...presentationsProps} />;
};
